import { useCallback, useEffect, useRef, useState } from "react";

export type RecorderPhase = "idle" | "recording" | "locked" | "stopped" | "error";

export interface VoiceRecorderState {
  phase: RecorderPhase;
  elapsedMs: number;
  liveLevels: number[];
  finalWaveform: number[] | null;
  finalBlob: Blob | null;
  finalDurationSec: number | null;
  finalExt: string | null; // matches finalBlob's actual container - server.js's ALLOWED_VOICE_EXT
  errorMessage: string | null;
}

export interface UseVoiceRecorder {
  state: VoiceRecorderState;
  start(): Promise<boolean>;
  stop(): void;
  lock(): void;
  discard(): void;
  reset(): void;
}

const LIVE_LEVELS_MAX = 48;
const FINAL_WAVEFORM_BARS = 48;

// audio/webm;codecs=opus (Chrome/Firefox/desktop Safari 14.1+) first, plain
// webm next, audio/mp4 last (iOS Safari, which doesn't support webm at
// all) - matched 1:1 against the extension the server's ALLOWED_VOICE_EXT
// accepts (server.js), since the extension is derived from this choice, not
// sniffed independently.
const MIME_CANDIDATES: Array<{ mimeType: string; ext: string }> = [
  { mimeType: "audio/webm;codecs=opus", ext: ".webm" },
  { mimeType: "audio/webm", ext: ".webm" },
  { mimeType: "audio/mp4", ext: ".m4a" },
];

function pickMimeType(): { mimeType: string; ext: string } | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const candidate of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate.mimeType)) return candidate;
  }
  return null;
}

function downsample(samples: number[], bars: number): number[] {
  if (!samples.length) return new Array(bars).fill(0);
  if (samples.length <= bars) {
    return [...samples, ...new Array(bars - samples.length).fill(samples[samples.length - 1] ?? 0)];
  }
  const step = samples.length / bars;
  const out: number[] = [];
  for (let i = 0; i < bars; i++) {
    const start = Math.floor(i * step);
    const end = Math.max(start + 1, Math.floor((i + 1) * step));
    const slice = samples.slice(start, end);
    out.push(slice.reduce((a, b) => a + b, 0) / slice.length);
  }
  return out;
}

const INITIAL_STATE: VoiceRecorderState = {
  phase: "idle",
  elapsedMs: 0,
  liveLevels: [],
  finalWaveform: null,
  finalBlob: null,
  finalDurationSec: null,
  finalExt: null,
  errorMessage: null,
};

export function useVoiceRecorder(): UseVoiceRecorder {
  const [state, setState] = useState<VoiceRecorderState>(INITIAL_STATE);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef(0);
  const chunksRef = useRef<Blob[]>([]);
  const accumulatorRef = useRef<number[]>([]);
  const mimeRef = useRef<{ mimeType: string; ext: string } | null>(null);

  const teardown = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (elapsedTimerRef.current !== null) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch {}
    }
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close().catch(() => {});
    }
    audioCtxRef.current = null;
  }, []);

  useEffect(() => teardown, [teardown]);

  const start = useCallback(async (): Promise<boolean> => {
    const picked = pickMimeType();
    if (!picked) {
      setState((s) => ({ ...s, phase: "error", errorMessage: "Voice recording isn't supported in this browser." }));
      return false;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setState((s) => ({ ...s, phase: "error", errorMessage: "Microphone access denied." }));
      return false;
    }

    mimeRef.current = picked;
    streamRef.current = stream;
    chunksRef.current = [];
    accumulatorRef.current = [];

    const recorder = new MediaRecorder(stream, { mimeType: picked.mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.start(250);
    recorderRef.current = recorder;

    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    audioCtxRef.current = audioCtx;

    const timeData = new Uint8Array(analyser.frequencyBinCount);
    const sampleLoop = () => {
      analyser.getByteTimeDomainData(timeData);
      let sumSquares = 0;
      for (let i = 0; i < timeData.length; i++) {
        const centered = (timeData[i] - 128) / 128;
        sumSquares += centered * centered;
      }
      const rms = Math.min(1, Math.sqrt(sumSquares / timeData.length) * 4); // *4: RMS of speech is quiet, boost for a visible bar
      accumulatorRef.current.push(rms);
      setState((s) => {
        if (s.phase !== "recording" && s.phase !== "locked") return s;
        const liveLevels = [...s.liveLevels, rms].slice(-LIVE_LEVELS_MAX);
        return { ...s, liveLevels };
      });
      rafRef.current = requestAnimationFrame(sampleLoop);
    };
    rafRef.current = requestAnimationFrame(sampleLoop);

    startedAtRef.current = Date.now();
    elapsedTimerRef.current = setInterval(() => {
      setState((s) => (s.phase === "recording" || s.phase === "locked" ? { ...s, elapsedMs: Date.now() - startedAtRef.current } : s));
    }, 50);

    setState({ ...INITIAL_STATE, phase: "recording" });
    return true;
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    const durationSec = Math.max(0, (Date.now() - startedAtRef.current) / 1000);
    const finalWaveform = downsample(accumulatorRef.current, FINAL_WAVEFORM_BARS);
    const ext = mimeRef.current?.ext ?? ".webm";
    const mimeType = mimeRef.current?.mimeType ?? "audio/webm";

    const finish = () => {
      const finalBlob = new Blob(chunksRef.current, { type: mimeType });
      teardown();
      setState((s) => ({
        ...s,
        phase: "stopped",
        finalBlob,
        finalWaveform,
        finalDurationSec: durationSec,
        finalExt: ext,
      }));
    };

    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = finish;
      recorder.stop();
    } else {
      finish();
    }
  }, [teardown]);

  const lock = useCallback(() => {
    setState((s) => (s.phase === "recording" ? { ...s, phase: "locked" } : s));
  }, []);

  const discard = useCallback(() => {
    teardown();
    setState(INITIAL_STATE);
  }, [teardown]);

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return { state, start, stop, lock, discard, reset };
}
